import { SBCRewardType } from "@/api/privateRequests/sbcs/getSbcSets";
import { SBCRequirements } from "@/components/organisms/SBCs/SBC/SBCRequirements";
import { SolutionStrategyTag } from "@/design-system/Tags/SolutionTag/SolutionTag";
import { cn } from "@/lib/utils";
import { SolutionTags } from "@/pages/Sbcs/components/Alternatives/SolutionTags";
import { SummaryFigures } from "@/pages/Sbcs/components/SummaryFigures";
import { Solution } from "@/redux/sbcs/sbcsSlice";
import { useTranslation } from "@repo/shared/i18n";
import { SolutionSummaryFacts, solutionSummaryFacts } from "@repo/shared/utils";
import { ReactNode } from "react";

import { SolutionReward } from "./SolutionReward";

/** The solver's totals in the shape every summary reads. */
export const solutionFactsOf = (solution?: Solution | null) =>
  solutionSummaryFacts({
    totalValue: solution?.cost ?? 0,
    transferCost: solution?.transferMarketCost ?? 0,
    ownedCount: solution?.ownedPlayersCount ?? 0,
    playerCount: solution?.players?.length ?? 0,
  });

/* The set card's eyebrow, so a section head reads the same in both places.
   Gold is the reward's own marker there, and stays exclusive to it here. */
const Label = ({ children, gold }: { children: string; gold?: boolean }) => (
  <span
    className={cn(
      "text-xxs font-semibold uppercase tracking-[0.14em]",
      gold ? "text-card-gold" : "text-foreground-faint",
    )}
  >
    {children}
  </span>
);

const Divider = () => <div className="h-px bg-edge-strong" />;

/**
 * Desktop: what the squad costs and what can be done with it, in a rail beside
 * the pitch rather than a band above it. Giving the figures a rail buys the
 * squad the whole width of the page, which is what lets the cards stay big
 * enough to read against the pitch. The Streamlined SBC page uses the same rail
 * beside its player rows.
 */
export const SolutionSidePanel = ({
  facts,
  recommended = false,
  showEstimate = false,
  tags,
  loading = false,
  actions,
  setReward,
  setDescription,
  requirements,
}: {
  facts: SolutionSummaryFacts;
  recommended?: boolean;
  /** The build-time figure, a Streamline idea. */
  showEstimate?: boolean;
  /** The pills under the figures. */
  tags: SolutionStrategyTag[];
  /** A solve is in flight; the figures show dashes. */
  loading?: boolean;
  actions: ReactNode;
  /** What the whole set pays out; the per-challenge reward is left out. */
  setReward?: SBCRewardType;
  /** Set blurb, used when the reward carries no description of its own. */
  setDescription?: string;
  /** The challenge's rules, as the set card lists them. */
  requirements: string[];
}) => {
  const { t } = useTranslation("sbcs");

  return (
    // A rail rather than a card: it runs the height of the page and is divided
    // from the squad by its own edge, so nothing floats inside a second frame.
    // A fourth figure (the estimate) needs the wider rail to stay on one line.
    <aside
      className={cn(
        "flex shrink-0 flex-col gap-5 border-r border-edge-strong bg-surface px-5 py-6",
        showEstimate ? "w-96" : "w-80",
      )}
    >
      <div className="flex flex-col gap-3">
        <SummaryFigures
          facts={facts}
          compact
          loading={loading}
          showEstimate={showEstimate}
        />
        <SolutionTags tags={tags} recommended={recommended} loading={loading} />
      </div>
      <Divider />
      {actions}
      {setReward && (
        <>
          <Divider />
          <SolutionReward
            reward={setReward}
            description={setDescription}
            label={<Label gold>{t("set.rewardLabel")}</Label>}
          />
        </>
      )}
      <Divider />
      <div className="flex flex-col gap-3">
        <Label>{t("sbcCard.requirements")}</Label>
        <SBCRequirements requirements={requirements} />
      </div>
    </aside>
  );
};
